import React, { useState, useMemo } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import { FaAngleLeft, FaAngleRight } from "react-icons/fa";
import "../assets/style/Timetable.css";
import SignUpForLessonModal from "../components/Timetable/SignUpForLessonModal";
import NotificationDialog from "../components/Timetable/SignUpForLessonModal/dialogs/NotificationDialog";
import StoreModal from "../components/StoreModal";
import SingleHour from "../components/Timetable/SingleHour";
import MobileTimetableView from "../components/Timetable/MobileTimetableView";
import { useAuthStore } from "../store";
import { useLocationStore } from "../hooks/useLocationStore";
import LoginModal from "../components/Login";
import useTimetableData from "../hooks/useTimetableData";
import { filterScheduleByLocationId } from "../utils/timetableUtils";

const TimeTable = () => {
  const [signUpForLessonOpen, setSignUpForLessonOpen] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);
  const [values, setValues] = useState({});
  const [lessonData, setLessonData] = useState({});
  const [isLoginOpen, setIsLoginOpen] = useState(false);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const { isAuthenticated } = useAuthStore((state) => ({
    isAuthenticated: state.isAuthenticated,
  }));

  // Use the globally selected location from the StudioSelector
  const { selectedLocationId, mainLocations, openStudioDialog } = useLocationStore(
    (state) => ({
      selectedLocationId: state.selectedLocationId,
      mainLocations: state.mainLocations,
      openStudioDialog: state.openStudioDialog,
    })
  );

  const selectedLocation = mainLocations.find(
    (l) => l.id === selectedLocationId
  );
  const showLocationBadge = mainLocations.length >= 2 && selectedLocation;

  const {
    schedule,
    weekDate,
    weekOffset,
    setWeekOffset,
    notification,
    setNotification,
    error,
  } = useTimetableData();

  // Filter the schedule to only show lessons for the selected location
  const filteredSchedule = useMemo(
    () => filterScheduleByLocationId(schedule, selectedLocationId),
    [schedule, selectedLocationId]
  );

  const handleOpenSignUpModal = (lesson) => {
    const currentTime = new Date();
    const lessonStartTime = new Date(
      lesson.start.year,
      lesson.start.month - 1,
      lesson.start.day,
      lesson.start.hour,
      lesson.start.minute,
      0
    );

    if (lessonStartTime <= currentTime) {
      setNotification({
        message: "Nie można zapisać się na zajęcia w przeszłości.",
        type: "error",
      });
      return;
    }

    if (!isAuthenticated) {
      setIsLoginOpen(true);
      return;
    }

    setLessonData({
      lesson_id: lesson.lesson_id,
      lesson_name: lesson.lesson_type.friendly_name,
      lesson_start: lesson.start,
      lesson_week_day: lesson.week_day,
      trainer: { full_name: lesson.trainer },
      lesson_type: lesson.lesson_type,
      start: lesson.flat_start,
      duration: lesson.lesson_duration,
      is_free: lesson.lesson_type.is_free,
      payment_on_site: lesson.payment_on_site,
      payment_only_online: lesson.payment_only_online,
      lesson_category_ids: lesson.lesson_type.lesson_categories_id || [],
      online_payment_amount: lesson.online_payment_amount,
      on_site_payment_amount: lesson.on_site_payment_amount,
      event_id: lesson.event_id,
      event_name: lesson.event_name,
      event_color: lesson.event_color,
    });
    setValues((prevValues) => ({
      ...prevValues,
      lesson_id: lesson.lesson_id,
      lesson_name: lesson.lesson_type.friendly_name,
      lesson_start: lesson.start,
      lesson_week_day: lesson.week_day,
      trainer: { full_name: lesson.trainer },
      lesson_type: lesson.lesson_type,
      start: lesson.flat_start,
      duration: lesson.lesson_duration,
      is_free: lesson.is_free,
      payment_on_site: lesson.payment_on_site,
      payment_only_online: lesson.payment_only_online,
      lesson_category_ids: lesson.lesson_type.lesson_categories_id || [],
    }));
    setSignUpForLessonOpen(true);
  };

  if (schedule.length === 0) return <p>Loading</p>;
  if (error) return <p>An error occurred</p>;

  return (
    <section id="timetable" className="d-flex align-items-center">
      <div className="container">
        <div className="section-title">
          <h2>Grafik / Zapisy</h2>
          {showLocationBadge && (
            <div className="timetable-location-badge">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="18"
                height="18"
              >
                <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
              </svg>
              <span>
                {selectedLocation.timetable_name || selectedLocation.name}
              </span>
              <button
                type="button"
                onClick={openStudioDialog}
                className="timetable-location-badge__change"
              >
                Zmień
              </button>
            </div>
          )}
        </div>

        <NotificationDialog
          open={!!notification}
          onClose={() => setNotification(null)}
          notification={notification}
        />

        {isMobile ? (
          <MobileTimetableView
            schedule={filteredSchedule}
            handleOpenSignUpModal={handleOpenSignUpModal}
            setValues={setValues}
            values={values}
            setLessonData={setLessonData}
            weekDate={weekDate}
            weekOffset={weekOffset}
            setWeekOffset={setWeekOffset}
          />
        ) : (
          <>
            {/* Desktop Week Navigation */}
            <div className="desktop-week-navigation">
              <button
                className="desktop-week-nav-btn prev-week"
                onClick={() => setWeekOffset(weekOffset - 1)}
                aria-label="Poprzedni tydzień"
              >
                <FaAngleLeft />
                <span className="nav-label">Poprzedni tydzień</span>
              </button>
              <div className="desktop-week-date">
                {weekDate.start} - {weekDate.end}
              </div>
              <button
                className="desktop-week-nav-btn next-week"
                onClick={() => setWeekOffset(weekOffset + 1)}
                aria-label="Następny tydzień"
              >
                <span className="nav-label">Następny tydzień</span>
                <FaAngleRight />
              </button>
            </div>

            {/* Desktop Timetable */}
            <div className="table-responsive">
              <table className="table table-bordered text-center white-table">
                <thead>
                  <tr className="bg-light-gray">
                    <th className="text-uppercase">
                      <div>Poniedziałek</div>
                      {weekDate.dates && weekDate.dates[0] && (
                        <div className="weekday-date">{weekDate.dates[0]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Wtorek</div>
                      {weekDate.dates && weekDate.dates[1] && (
                        <div className="weekday-date">{weekDate.dates[1]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Środa</div>
                      {weekDate.dates && weekDate.dates[2] && (
                        <div className="weekday-date">{weekDate.dates[2]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Czwartek</div>
                      {weekDate.dates && weekDate.dates[3] && (
                        <div className="weekday-date">{weekDate.dates[3]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Piątek</div>
                      {weekDate.dates && weekDate.dates[4] && (
                        <div className="weekday-date">{weekDate.dates[4]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Sobota</div>
                      {weekDate.dates && weekDate.dates[5] && (
                        <div className="weekday-date">{weekDate.dates[5]}</div>
                      )}
                    </th>
                    <th className="text-uppercase">
                      <div>Niedziela</div>
                      {weekDate.dates && weekDate.dates[6] && (
                        <div className="weekday-date">{weekDate.dates[6]}</div>
                      )}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {Object.keys(filteredSchedule.objects).length > 0 ? (
                    Object.keys(filteredSchedule.objects).map((key) => {
                      let lessons = filteredSchedule.objects[key];
                      return (
                        <SingleHour
                          key={key}
                          hour={lessons}
                          handleOpenSignUpModal={handleOpenSignUpModal}
                          setValues={setValues}
                          values={values}
                          setLessonData={setLessonData}
                        />
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="7" style={{ padding: '3rem 1rem', textAlign: 'center', color: '#888' }}>
                        <div style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                          Brak zajęć w tym tygodniu{selectedLocation ? ` dla lokalizacji "${selectedLocation.timetable_name || selectedLocation.name}"` : ''}.
                        </div>
                        <div style={{ fontSize: '0.9rem' }}>
                          Spróbuj wybrać inny tydzień lub zmienić lokalizację.
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      <SignUpForLessonModal
        open={signUpForLessonOpen}
        onClose={() => setSignUpForLessonOpen(false)}
        setNotification={setNotification}
        lessonData={lessonData}
        setLessonData={setLessonData}
      />
      <StoreModal
        open={storeModalOpen}
        onClose={() => setStoreModalOpen(false)}
      />
      <LoginModal
        open={isLoginOpen}
        handleClose={() => setIsLoginOpen(false)}
        showAlert={(message, type) => {
          setNotification({ message, type });
        }}
      />
    </section>
  );
};

export default TimeTable;
